import { gql, useMutation } from '@apollo/client';
import { useNavigation } from '@react-navigation/core';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import { RETRY_CHARGING_PAYMENT } from 'graphql/mutations/retryChargingPayment';
import { READ_CHARGING_SESSION } from 'graphql/queries/readChargingSession';
import { useContext } from 'react';
import { useRetryPaymentStore } from 'stores/retryPaymentStore';
import {
  Mutation,
  MutationRetryFailedPaymentV2Args,
} from 'types/generatedSchemaTypes';
import { AccountScreenNavigationProp, SetParamsProps } from 'types/navigation';
import { updateCache } from 'utils/cacheUtils';

type RetryChargingPaymentProps = {
  chargingSessionId?: string;
};
const useRetryChargingPaymentMutation = ({
  chargingSessionId,
}: RetryChargingPaymentProps) => {
  const { refetchCustomer } = useContext(AccountContext);
  const { setParams, navigate } = useNavigation() as SetParamsProps &
    Pick<AccountScreenNavigationProp, 'navigate'>;
  const { startChargingPolling } = useRetryPaymentStore();
  const [retryChargingPayment, { loading: retryPaymentLoading }] = useMutation<
    Mutation,
    MutationRetryFailedPaymentV2Args
  >(RETRY_CHARGING_PAYMENT, {
    refetchQueries: chargingSessionId
      ? [
          {
            query: READ_CHARGING_SESSION,
            variables: { sessionId: chargingSessionId },
          },
        ]
      : [],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      const { transactionId } = data?.retryFailedPaymentV2 ?? {};
      if (transactionId) {
        setParams({ transactionId });
        updateCache(
          cache,
          { __typename: 'ChargingSession', chargingSessionId },
          {
            fragment: gql`
              fragment ChargingSessionFragment on ChargingSession {
                transactionId
              }
            `,
            data: { transactionId },
          }
        );
      }
    },
    onCompleted: async (data) => {
      const result = data?.retryFailedPaymentV2;
      const isResolved = result?.transactionId != null;

      if (isResolved) {
        showToast('parking.receipt.retrySuccessfulDescription', 'success');
      }
      if (chargingSessionId) {
        startChargingPolling(chargingSessionId);
        navigate('accountScreen', {
          screen: 'parkingHistory',
          params: { filter: 'failed' },
        });
      }
    },
    onError: (error) => {
      if (
        error.graphQLErrors.some((e) => e.extensions?.code === 'CARD_BLOCKED')
      ) {
        showToast('error.cardBlocked', 'info');
        refetchCustomer();
      } else {
        showToast('parking.receipt.retryUnsuccessfulDescription', 'error');
      }
    },
  });
  return { retryChargingPayment, retryPaymentLoading };
};

export default useRetryChargingPaymentMutation;
